/**
 * Everything that has been measured, most recent first.
 *
 * Every value carries its unit (§16.1) — `observationValue` in web-kit builds
 * the text from the stored Quantity, so a number never appears here without
 * one. A bare number representing a glucose is §1.2's bug, and it is a worse
 * bug on a patient's screen than anywhere else: a clinician might catch a
 * missing "mmol/L", a patient will not.
 *
 * The labels are the codes' own displays. A friendlier map from LOINC to
 * everyday English would read better and is explicitly forbidden here —
 * §8.0.1 allows no code list in application code, "not in a constant, not in
 * an enum, not in a switch", and CI enforces it. If "Hemoglobin A1c/Hemoglobin
 * .total in Blood" is the wrong thing to show a patient, the fix is a
 * designation in `ig/`, where it can be reviewed as terminology.
 */

import { type JSX } from 'react';

import { byRecencyDesc, codeText, observationValue } from '@glycemia/web-kit';

import { Async } from '../components/Async.js';
import { Value } from '../components/DisplayValue.js';
import { useMyObservations } from '../lib/queries.js';

export const MyResults = ({ patientId }: { readonly patientId: string }): JSX.Element => {
  const observations = useMyObservations(patientId);

  return (
    <section aria-labelledby="results-heading">
      <h1 id="results-heading">My results</h1>

      <Async query={observations} empty="No results are recorded for you yet.">
        {(items) => (
          <table>
            <caption>{items.length} result(s), most recent first</caption>
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Test</th>
                <th scope="col">Result</th>
              </tr>
            </thead>
            <tbody>
              {byRecencyDesc(items).map((observation) => (
                <tr key={observation.id}>
                  <td>{observation.effectiveDateTime ?? 'Undated'}</td>
                  <td>{codeText(observation.code)}</td>
                  <td>
                    <Value value={observationValue(observation)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Async>

      <p className="notice" role="note">
        A result on its own does not tell you whether anything needs to change. Your care team reads
        these alongside the rest of your record.
      </p>
    </section>
  );
};
